import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { I18n } from "../../models/I18n";
import AbilityToChangeSummary from "./components/AbilityToChangeSummary/AbilityToChangeSummary";
import CompetencySummary from "./components/CompetencySummary/CompetencySummary";
import IntellectualCapabilitySummary from "./components/IntellectualCapabilitySummary/IntellectualCapabilitySummary";
import ListSummary from "./components/ListSummary/ListSummary";
import PersonalitySummary from "./components/PersonalitySummary/PersonalitySummary";
import Reflection from "./components/Reflection/Reflection";
import { ReportSummaryColors } from "./constants/ReportSummaryColors";
import { ReportConfigVisibility } from "./constants/ReportConfigVisibility";
import style from "./style/ReportSummary.module.scss";
import { SideNavigator } from "./components/SideNavigator";
import AdviceSummary from "./components/AdviceSummary/AdviceSummary";
import { ReportConfig } from "./models/ReportConfig";
import {AdviceSectionTypes} from "./constants/AdviceSectionTypes";

interface ReportSummaryProps {
    scrollToSection: (elementId: string) => void;
    reportData: any;
    reflectionEditable: boolean;
    textSaveInterval?: number;
    submitReflectionText?: (data: { value: string }) => Promise<any>;
    i18n: I18n;
}

const ReportSummary = ({
    scrollToSection,
    reportData,
    reflectionEditable,
    textSaveInterval,
    submitReflectionText,
    i18n
}: ReportSummaryProps) => {
    const createDOMPurify = require("isomorphic-dompurify");
    const DOMPurify = createDOMPurify(window);

    const showSection = (section: any) => {
        if (!section?.handle) {
            return false;
        }
        const sectionConfig = reportData.config.find((config: ReportConfig) => config.i === section.handle);

        return !!sectionConfig && sectionConfig.visibility !== ReportConfigVisibility.FULL;
    };

    // One report can have only one of these sections in hybrid assessments
    const getAdviceSectionTypes = () => {
        if (reportData.sections.selectionAdvice) {
            return AdviceSectionTypes.SELECTION_ADVICE;
        } else if (reportData.sections.developmentAdvice) {
            return AdviceSectionTypes.DEVELOPMENT_ADVICE;
        } else if (reportData.sections.selectionDevelopmentAdvice) {
            return AdviceSectionTypes.SELECTION_DEVELOPMENT_ADVICE;
        }

        return null;
    }

    const adviceSection = getAdviceSectionTypes() && reportData.sections[getAdviceSectionTypes()];
    const selectionText = adviceSection?.text || adviceSection?.selectionText;
    const developmentText = adviceSection?.text || adviceSection?.developmentText;

    return (
        <div className={style.reportSummaryWrapper}>
            <div className={style.reportSummary}>
                <div className={style.introduction}>{i18n.report_management_summary_introduction}</div>
                <div className={style.reportSummaryContents}>
                    {showSection(adviceSection) && <div className={style.summarySection}>
                        <AdviceSummary
                            title={adviceSection.summaryName}
                            basedOn={adviceSection.basedOn}
                            handle={adviceSection.handle}
                            selectionText={selectionText}
                            developmentText={developmentText}
                            evaluationText={adviceSection.scores?.length ? adviceSection.scores[0].evaluation : ""}
                            evaluation={adviceSection.scores?.length ? adviceSection.scores[0].score : ""}
                            sectionType={getAdviceSectionTypes()}
                            i18n={i18n}
                        />
                    </div>}
                    <div className={style.reportSummaryGridSection}>
                        <div>
                            <div>
                                {showSection(reportData.sections.strongPoints) && (
                                    <div className={style.summarySection}>
                                        <ListSummary
                                            basedOn={reportData.sections.strongPoints.basedOn}
                                            circleColor={ReportSummaryColors.GREEN}
                                            handle={reportData.sections.strongPoints.handle}
                                            i18n={i18n}
                                            showViewAll={false}
                                            title={reportData.sections.strongPoints.summaryName}
                                        >
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        reportData.sections.strongPoints.text ||
                                                        reportData.sections.strongPoints.autoCalculatedText,
                                                    ),
                                                }}
                                            />
                                        </ListSummary>
                                    </div>
                                )}
                                {showSection(reportData.sections.pointsOfAttention) && (
                                    <div className={style.summarySection}>
                                        <ListSummary
                                            basedOn={reportData.sections.pointsOfAttention.basedOn}
                                            circleColor={ReportSummaryColors.ORANGE}
                                            handle={reportData.sections.pointsOfAttention.handle}
                                            i18n={i18n}
                                            showViewAll={false}
                                            title={reportData.sections.pointsOfAttention.summaryName}
                                        >
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        reportData.sections.pointsOfAttention.text ||
                                                        reportData.sections.pointsOfAttention.autoCalculatedText,
                                                    ),
                                                }}
                                            />
                                        </ListSummary>
                                    </div>
                                )}
                            </div>
                            <div>
                                <div>
                                    {showSection(reportData.sections.abilityToChangeCalculated) && (
                                        <div className={style.summarySection}>
                                            <AbilityToChangeSummary
                                                basedOn={reportData.sections.abilityToChangeCalculated.basedOn}
                                                handle={reportData.sections.abilityToChangeCalculated.handle}
                                                i18n={i18n}
                                                outcome={reportData.sections.abilityToChangeCalculated.outcome}
                                                scores={reportData.sections.abilityToChangeCalculated.scores}
                                                scrollToSection={scrollToSection}
                                                title={reportData.sections.abilityToChangeCalculated.summaryName}
                                            />
                                        </div>
                                    )}
                                    {showSection(reportData.sections.intellectualCapability) && (
                                        <div className={style.summarySection}>
                                            <IntellectualCapabilitySummary
                                                basedOn={reportData.sections.intellectualCapability.basedOn}
                                                cutOff={reportData.sections.intellectualCapability.cutOff}
                                                evaluation={reportData.sections.intellectualCapability.evaluation}
                                                handle={reportData.sections.intellectualCapability.handle}
                                                i18n={i18n}
                                                outcome={reportData.sections.intellectualCapability.outcome}
                                                score={
                                                    reportData.sections.intellectualCapability.scoreTypes?.norm5 ||
                                                    reportData.sections.intellectualCapability.score
                                                }
                                                scrollToSection={scrollToSection}
                                                title={reportData.sections.intellectualCapability.summaryName}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    {showSection(reportData.sections.personality) && (
                                        <div className={style.summarySection}>
                                            <PersonalitySummary
                                                basedOn={reportData.sections.personality.basedOn}
                                                handle={reportData.sections.personality.handle}
                                                i18n={i18n}
                                                scores={reportData.sections.personality.scores}
                                                scrollToSection={scrollToSection}
                                                title={reportData.sections.personality.summaryName}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {showSection(reportData.sections.competencyProfile) && (
                            <div className={style.summarySection}>
                                <CompetencySummary
                                    basedOn={reportData.sections.competencyProfile.basedOn}
                                    competencies={reportData.sections.competencyProfile.competencies}
                                    handle={reportData.sections.competencyProfile.handle}
                                    i18n={i18n}
                                    languageId={reportData.participant.language.replace("-", "_")}
                                    scrollToSection={scrollToSection}
                                    title={reportData.sections.competencyProfile.summaryName}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showSection(reportData.sections.reflection) && (
                <Reflection
                    editable={reflectionEditable}
                    i18n={i18n}
                    participant={reportData.participant}
                    submitReflectionText={submitReflectionText}
                    text={reportData.sections.reflection.text}
                    title={reportData.sections.reflection.summaryName}
                    textSaveInterval={textSaveInterval}
                />
            )}
            <div className={style.showFullReport}>
                <span role="button" tabIndex={1} onClick={() => scrollToSection("assessment_report_content")} data-testid="scroll-to-report-content button">
                    {i18n.report_management_summary_full_report_button}
                </span>
                <div
                    className={style.showFullReportButtonIcon}
                    role="button"
                    tabIndex={1}
                    data-testid="scroll-to-report-content icon"
                    onClick={() => scrollToSection("assessment_report_content")}
                >
                    <FontAwesomeIcon icon={faChevronDown} />
                </div>
            </div>
        </div>
    );
};

ReportSummary.SideNavigator = SideNavigator;

export default ReportSummary;
